import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  CheckCircle2,
  XCircle,
  MinusCircle,
  HelpCircle,
  Globe,
  Package,
  HeartHandshake,
  Search,
  Share2,
  Smartphone,
  Megaphone,
  Info,
  Lightbulb,
  Target,
  Zap,
} from "lucide-react";
import { cn, getScoreColor } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import QUESTION_TIPS from "@/lib/questionTips";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-audit-tool/artifacts/aj4ztq87_image.png";

const SECTION_ICON_MAP = {
  discover: Globe,
  deliver: Package,
  delight: HeartHandshake,
  dominate: Search,
  discourse: Share2,
  mobile: Smartphone,
  drive: Megaphone,
};

const SECTION_META = {
  discover: { title: "Website Presence & Product Experience", color: "#FACC15" },
  mobile: { title: "Mobile Optimization", color: "#F97316" },
  drive: { title: "Marketing Readiness", color: "#A855F7" },
  deliver: { title: "Post-Purchase & Packaging", color: "#22C55E" },
  delight: { title: "Customer Support", color: "#EC4899" },
  dominate: { title: "Search Visibility (SEO)", color: "#3B82F6" },
  discourse: { title: "Social Footprint", color: "#8B5CF6" },
};

const ANSWER_OPTIONS = [
  { value: "yes", label: "Yes", icon: CheckCircle2, color: "text-green-400", bgColor: "bg-green-500/10", borderColor: "border-green-500/40", hoverBg: "hover:bg-green-500/20" },
  { value: "partial", label: "Partial", icon: MinusCircle, color: "text-yellow-400", bgColor: "bg-yellow-500/10", borderColor: "border-yellow-500/40", hoverBg: "hover:bg-yellow-500/20" },
  { value: "no", label: "No", icon: XCircle, color: "text-red-400", bgColor: "bg-red-500/10", borderColor: "border-red-500/40", hoverBg: "hover:bg-red-500/20" },
  { value: "na", label: "N/A", icon: HelpCircle, color: "text-zinc-400", bgColor: "bg-zinc-500/10", borderColor: "border-zinc-500/40", hoverBg: "hover:bg-zinc-500/20" },
];

function QuestionTipPopover({ questionId, children }) {
  const tip = QUESTION_TIPS[questionId];
  
  if (!tip) return children;
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent 
        side="right" 
        align="start"
        className="w-96 p-0 bg-zinc-900 border-zinc-700 shadow-2xl"
      >
        <div className="p-4 border-b border-zinc-800 bg-zinc-800/50">
          <div className="flex items-center gap-2 text-primary">
            <Lightbulb className="w-5 h-5" />
            <span className="font-semibold">Implementation Guide</span>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-blue-400">How to Implement</span>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed">{tip.howTo}</p>
          </div>
          <div className="pt-3 border-t border-zinc-800">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <span className="text-sm font-semibold text-green-400">Benefits</span>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed">{tip.benefit}</p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function AuditSection() {
  const { auditId, sectionId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [audit, setAudit] = useState(null);
  const [sectionDef, setSectionDef] = useState(null);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState({});
  const [aiAnswered, setAiAnswered] = useState({});
  const [currentSubsection, setCurrentSubsection] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [showRecs, setShowRecs] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    if (!authLoading) fetchData();
  }, [auditId, sectionId, authLoading, isAuthenticated]);

  const fetchData = async () => {
    try {
      const [auditRes, sectionRes] = await Promise.all([
        axios.get(`${API}/audits/${auditId}`),
        axios.get(`${API}/sections/${sectionId}`),
      ]);
      
      setAudit(auditRes.data);
      setSectionDef(sectionRes.data);
      
      // Load recommendations if they exist
      if (auditRes.data.recommendations?.[sectionId]) {
        setRecommendations(auditRes.data.recommendations[sectionId]);
      }
      
      // Load existing answers
      const sectionData = auditRes.data.sections[sectionId];
      if (sectionData && sectionData.answers) {
        const answersMap = {};
        const notesMap = {};
        const aiMap = {};
        sectionData.answers.forEach((a) => {
          answersMap[a.question_id] = a.answer;
          notesMap[a.question_id] = a.notes || "";
          aiMap[a.question_id] = a.ai_answered || false;
        });
        setAnswers(answersMap);
        setNotes(notesMap);
        setAiAnswered(aiMap);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load section");
      navigate(`/dashboard/${auditId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    // Remove AI badge when user manually changes the answer
    setAiAnswered((prev) => ({ ...prev, [questionId]: false }));
  };

  const handleNoteChange = (questionId, value) => {
    setNotes((prev) => ({ ...prev, [questionId]: value }));
  };

  const saveAnswers = async () => {
    setSaving(true);
    try {
      const answersList = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer,
        notes: notes[question_id] || "",
        ai_answered: aiAnswered[question_id] || false,
      }));

      await axios.put(`${API}/audits/${auditId}/sections/${sectionId}`, answersList);
      toast.success("Progress saved!");
      
      // Refresh audit data
      const response = await axios.get(`${API}/audits/${auditId}`);
      setAudit(response.data);
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save progress");
    } finally {
      setSaving(false);
    }
  };

  const goToNextSubsection = () => {
    if (currentSubsection < sectionDef.subsections.length - 1) {
      setCurrentSubsection(currentSubsection + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goToPrevSubsection = () => {
    if (currentSubsection > 0) {
      setCurrentSubsection(currentSubsection - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  if (!audit || !sectionDef) return null;

  const Icon = SECTION_ICON_MAP[sectionId];
  const meta = SECTION_META[sectionId];
  const currentSub = sectionDef.subsections[currentSubsection];
  const sectionData = audit.sections[sectionId];
  
  const totalQuestions = sectionDef.subsections.reduce((acc, sub) => acc + sub.questions.length, 0);
  const answeredQuestions = Object.keys(answers).filter(k => answers[k]).length;
  const progressPercentage = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/dashboard/${auditId}`)}
                className="text-zinc-400 hover:text-white"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${meta.color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: meta.color }} />
                </div>
                <div>
                  <h1 className="heading-sm text-white">{sectionDef.name}</h1>
                  <p className="text-xs text-zinc-500">{meta.title}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-sm">
                <span className="text-zinc-400">Score:</span>
                <span className={cn("font-bold", getScoreColor(sectionData.score))}>
                  {Math.round(sectionData.score)}%
                </span>
              </div>
              <Button
                onClick={saveAnswers}
                disabled={saving}
                className="btn-primary"
                data-testid="save-progress-btn"
              >
                {saving ? (
                  <div className="spinner w-4 h-4" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Sidebar - Subsection Navigation */}
          <div className="lg:col-span-3">
            <Card className="card-glass sticky top-24">
              <CardHeader>
                <CardTitle className="heading-sm text-zinc-300">Subsections</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] custom-scrollbar">
                  <div className="space-y-2">
                    {sectionDef.subsections.map((sub, index) => {
                      const subAnswered = sub.questions.filter(q => answers[q.id]).length;
                      const subTotal = sub.questions.length;
                      const isComplete = subAnswered === subTotal;
                      
                      return (
                        <button
                          key={index}
                          onClick={() => setCurrentSubsection(index)}
                          className={cn(
                            "w-full text-left p-3 rounded-lg transition-all",
                            currentSubsection === index
                              ? "bg-primary/20 border border-primary/30"
                              : "hover:bg-zinc-800/50"
                          )}
                          data-testid={`subsection-nav-${index}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className={cn(
                              "text-sm font-medium",
                              currentSubsection === index ? "text-primary" : "text-zinc-300"
                            )}>
                              {sub.name}
                            </span>
                            {isComplete && (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Progress value={(subAnswered / subTotal) * 100} className="h-1 flex-1" />
                            <span className="text-xs text-zinc-500">{subAnswered}/{subTotal}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
                
                {/* Overall Progress */}
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-400">Section Progress</span>
                    <span className="text-white">{answeredQuestions}/{totalQuestions}</span>
                  </div>
                  <Progress value={progressPercentage} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content - Questions */}
          <div className="lg:col-span-9">
            <Card className="card-glass overflow-hidden" data-testid="questions-card">
              <CardHeader className="border-b border-zinc-800 bg-zinc-900/50">
                <div className="flex items-center justify-between">
                  <div>
                    <Badge 
                      variant="outline" 
                      className="mb-2 border-zinc-700 text-zinc-400"
                    >
                      Subsection {currentSubsection + 1} of {sectionDef.subsections.length}
                    </Badge>
                    <CardTitle className="heading-md text-white">{currentSub.name}</CardTitle>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-zinc-500">Questions</p>
                    <p className="text-lg font-bold text-white">
                      {currentSub.questions.filter(q => answers[q.id]).length}/{currentSub.questions.length}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-zinc-800/50">
                  {currentSub.questions.map((question, qIndex) => {
                    const currentAnswer = answers[question.id];
                    const hasTip = QUESTION_TIPS[question.id];
                    
                    return (
                      <div
                        key={question.id}
                        className={cn(
                          "p-6 transition-all",
                          currentAnswer 
                            ? "bg-zinc-900/30" 
                            : "bg-transparent hover:bg-zinc-900/20"
                        )}
                        data-testid={`question-${question.id}`}
                      >
                        {/* Question Header */}
                        <div className="flex items-start gap-4 mb-5">
                          <div 
                            className={cn(
                              "flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all",
                              currentAnswer 
                                ? "bg-primary/20 text-primary" 
                                : "bg-zinc-800 text-zinc-500"
                            )}
                          >
                            {qIndex + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-base leading-relaxed pr-8">{question.text}</p>
                            <div className="flex items-center gap-3 mt-2">
                              {question.weight && question.weight !== 1 && (
                                <span className="inline-flex items-center text-xs px-2 py-1 rounded-md bg-primary/15 text-primary font-medium">
                                  Weight: {question.weight}x
                                </span>
                              )}
                              {aiAnswered[question.id] && (
                                <span className="inline-flex items-center text-xs px-2 py-1 rounded-md bg-violet-500/15 text-violet-400 font-medium" data-testid={`ai-badge-${question.id}`}>
                                  <Zap className="w-3 h-3 mr-1" />
                                  AI Audited
                                </span>
                              )}
                            </div>
                          </div>
                          {hasTip && (
                            <QuestionTipPopover questionId={question.id}>
                              <button 
                                className="flex-shrink-0 w-8 h-8 rounded-full bg-zinc-800 hover:bg-primary/20 text-zinc-500 hover:text-primary flex items-center justify-center transition-all"
                                data-testid={`info-btn-${question.id}`}
                              >
                                <Info className="w-4 h-4" />
                              </button>
                            </QuestionTipPopover>
                          )}
                        </div>
                        
                        {/* Answer Buttons */}
                        <div className="flex flex-wrap gap-3 mb-5 pl-14">
                          {ANSWER_OPTIONS.map((option) => {
                            const OptionIcon = option.icon;
                            const isSelected = currentAnswer === option.value;
                            
                            return (
                              <button
                                key={option.value}
                                onClick={() => handleAnswer(question.id, option.value)}
                                className={cn(
                                  "flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 transition-all font-medium",
                                  isSelected
                                    ? `${option.bgColor} ${option.borderColor} ${option.color} scale-105 shadow-lg`
                                    : `border-zinc-700 text-zinc-400 ${option.hoverBg} hover:border-zinc-600`
                                )}
                                data-testid={`answer-${question.id}-${option.value}`}
                              >
                                <OptionIcon className={cn("w-4 h-4", isSelected && option.color)} />
                                <span className="text-sm">{option.label}</span>
                              </button>
                            );
                          })}
                        </div>
                        
                        {/* AI Note (if auto-audited) */}
                        {notes[question.id] && notes[question.id].startsWith("[AI]") && (
                          <div className="pl-14 mb-2">
                            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-500/20">
                              <Zap className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-violet-300">{notes[question.id].replace("[AI] ", "")}</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Notes */}
                        <div className="pl-14">
                          <Textarea
                            placeholder="Add notes or observations (optional)..."
                            value={notes[question.id]?.startsWith("[AI]") ? "" : (notes[question.id] || "")}
                            onChange={(e) => handleNoteChange(question.id, e.target.value)}
                            className="bg-zinc-950/50 border-zinc-800 text-white placeholder:text-zinc-600 min-h-[50px] resize-none rounded-xl focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
                            data-testid={`notes-${question.id}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* AI Recommendations Panel */}
            <Card className="card-glass mt-4" data-testid="recommendations-panel">
              <CardContent className="p-4">
                <button
                  onClick={() => {
                    if (!recommendations && !loadingRecs) {
                      setLoadingRecs(true);
                      setShowRecs(true);
                      // First try to fetch cached recommendations
                      axios.get(`${API}/audits/${auditId}/recommendations`)
                        .then(resp => {
                          const cached = resp.data.recommendations?.[sectionId];
                          if (cached?.recommendations?.length) {
                            setRecommendations(cached);
                            setLoadingRecs(false);
                          } else {
                            // Generate fresh
                            return axios.post(`${API}/audits/${auditId}/recommendations/generate`)
                              .then(genResp => {
                                const recs = genResp.data.recommendations?.[sectionId];
                                setRecommendations(recs);
                              });
                          }
                        })
                        .catch(() => toast.error("Failed to generate recommendations"))
                        .finally(() => setLoadingRecs(false));
                    } else {
                      setShowRecs(!showRecs);
                    }
                  }}
                  className="flex items-center justify-between w-full"
                  data-testid="toggle-recommendations-btn"
                >
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm font-semibold text-white">AI Recommendations</span>
                    {recommendations && (
                      <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 text-xs">
                        {recommendations.recommendations?.length || 0}
                      </Badge>
                    )}
                  </div>
                  <span className="text-zinc-500 text-xs">{showRecs ? "Hide" : loadingRecs ? "Generating..." : recommendations ? "Show" : "Generate"}</span>
                </button>
                {showRecs && (
                  <div className="mt-4 space-y-3">
                    {loadingRecs && (
                      <div className="flex items-center gap-2 text-zinc-400 text-sm py-4 justify-center">
                        <Zap className="w-4 h-4 animate-pulse text-yellow-400" />
                        Analyzing section and generating recommendations...
                      </div>
                    )}
                    {recommendations?.recommendations?.map((rec, i) => (
                      <div key={i} className="rounded-xl bg-zinc-800/50 border border-zinc-700/40 p-3.5">
                        <div className="flex items-start justify-between mb-1.5">
                          <span className="text-white text-sm font-medium">{rec.title}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            {rec.impact && (
                              <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">{rec.impact}</span>
                            )}
                            <span className={cn(
                              "text-xs px-2 py-0.5 rounded-full",
                              rec.priority === "high" ? "bg-red-500/20 text-red-300" :
                              rec.priority === "medium" ? "bg-yellow-500/20 text-yellow-300" :
                              "bg-zinc-500/20 text-zinc-400"
                            )}>
                              {rec.priority}
                            </span>
                          </div>
                        </div>
                        <p className="text-zinc-400 text-xs leading-relaxed">{rec.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Navigation */}
                <div className="flex items-center justify-between p-6 bg-zinc-900/50 border-t border-zinc-800">
                  <Button
                    variant="outline"
                    onClick={goToPrevSubsection}
                    disabled={currentSubsection === 0}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    data-testid="prev-subsection-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-2">
                    {sectionDef.subsections.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSubsection(index)}
                        className={cn(
                          "w-2 h-2 rounded-full transition-all",
                          currentSubsection === index ? "bg-primary w-6" : "bg-zinc-700 hover:bg-zinc-600"
                        )}
                        data-testid={`subsection-dot-${index}`}
                      />
                    ))}
                  </div>
                  
                  {currentSubsection < sectionDef.subsections.length - 1 ? (
                    <Button
                      onClick={goToNextSubsection}
                      className="btn-primary"
                      data-testid="next-subsection-btn"
                    >
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        saveAnswers();
                        navigate(`/dashboard/${auditId}`);
                      }}
                      className="btn-primary"
                      data-testid="finish-section-btn"
                    >
                      Finish Section
                      <CheckCircle2 className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
